export const RUTGERS_GRADE_POINTS = {
  A: 4,
  'B+': 3.5,
  B: 3,
  'C+': 2.5,
  C: 2,
  D: 1,
  F: 0,
} as const;

export const RUTGERS_LETTER_GRADES = Object.keys(RUTGERS_GRADE_POINTS) as RutgersLetterGrade[];
export const RUTGERS_NON_GPA_DESIGNATIONS = ['P', 'NC', 'E', 'J', 'W', 'IN', 'TZ', 'N/A'] as const;
export const RUTGERS_GRADE_OPTIONS = [...RUTGERS_LETTER_GRADES, ...RUTGERS_NON_GPA_DESIGNATIONS] as const;

export type RutgersLetterGrade = keyof typeof RUTGERS_GRADE_POINTS;
export type RutgersNonGpaDesignation = (typeof RUTGERS_NON_GPA_DESIGNATIONS)[number];
export type RutgersCourseDesignation = RutgersNonGpaDesignation | 'K' | 'STANDARD';

export interface RutgersGpaCourse {
  credits: number;
  grade?: string | null;
  designation?: RutgersCourseDesignation | string | null;
  includeInGpa?: boolean;
}

export interface RutgersGpaResult {
  rawGpa: number | null;
  displayGpa: string;
  gradedCredits: number;
  gradePoints: number;
  excludedCredits: number;
  invalidEntries: number;
}

export function isRutgersLetterGrade(value: unknown): value is RutgersLetterGrade {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(RUTGERS_GRADE_POINTS, value.toUpperCase());
}

export function roundRutgersGpa(value: number): number {
  return Math.round((value + Number.EPSILON) * 1000) / 1000;
}

export function formatRutgersGpa(value: number | null): string {
  return value == null || !Number.isFinite(value) ? 'N/A' : roundRutgersGpa(value).toFixed(3);
}

function finiteCredits(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function calculateRutgersGpa(courses: RutgersGpaCourse[]): RutgersGpaResult {
  let gradePoints = 0;
  let gradedCredits = 0;
  let excludedCredits = 0;
  let invalidEntries = 0;

  for (const course of courses) {
    const credits = finiteCredits(Number(course.credits));
    if (!credits) continue;

    const grade = String(course.grade ?? '').trim().toUpperCase();
    const designation = String(course.designation ?? 'STANDARD').trim().toUpperCase();
    const isExcludedDesignation = designation !== 'STANDARD' && designation !== 'K';

    // AP equivalencies are written into the prerequisite record as a marker,
    // not a Rutgers letter grade. Keep their credits out of GPA without
    // treating the marker as a malformed transcript grade.
    if (grade === 'AP') {
      excludedCredits += credits;
      continue;
    }

    if (course.includeInGpa === false || isExcludedDesignation || RUTGERS_NON_GPA_DESIGNATIONS.includes(grade as RutgersNonGpaDesignation)) {
      excludedCredits += credits;
      continue;
    }

    if (!isRutgersLetterGrade(grade)) {
      if (grade) invalidEntries += 1;
      continue;
    }

    gradePoints += RUTGERS_GRADE_POINTS[grade] * credits;
    gradedCredits += credits;
  }

  const rawGpa = gradedCredits > 0 ? gradePoints / gradedCredits : null;
  return {
    rawGpa,
    displayGpa: formatRutgersGpa(rawGpa),
    gradedCredits,
    gradePoints,
    excludedCredits,
    invalidEntries,
  };
}

export type TargetGpaStatus = 'achievable' | 'already-met' | 'impossible' | 'invalid';

export interface TargetGpaResult {
  requiredGpa: number | null;
  status: TargetGpaStatus;
}

export function calculateRequiredGpa(input: {
  currentGpa: number;
  currentGradedCredits: number;
  targetGpa: number;
  plannedGradedCredits: number;
}): TargetGpaResult {
  const { currentGpa, currentGradedCredits, targetGpa, plannedGradedCredits } = input;
  if (
    ![currentGpa, currentGradedCredits, targetGpa, plannedGradedCredits].every(Number.isFinite) ||
    currentGpa < 0 || currentGpa > 4 || targetGpa < 0 || targetGpa > 4 ||
    currentGradedCredits < 0 || plannedGradedCredits <= 0
  ) {
    return { requiredGpa: null, status: 'invalid' };
  }

  const requiredGpa = (
    targetGpa * (currentGradedCredits + plannedGradedCredits) - currentGpa * currentGradedCredits
  ) / plannedGradedCredits;

  if (requiredGpa > 4) return { requiredGpa, status: 'impossible' };
  if (requiredGpa <= 0) return { requiredGpa: 0, status: 'already-met' };
  return { requiredGpa, status: 'achievable' };
}

export function calculateProjectedGpa(input: {
  currentGpa: number;
  currentGradedCredits: number;
  plannedCourses: RutgersGpaCourse[];
}): RutgersGpaResult {
  const planned = calculateRutgersGpa(input.plannedCourses);
  const currentCredits = finiteCredits(input.currentGradedCredits);
  const currentGradePoints = Number.isFinite(input.currentGpa) && input.currentGpa >= 0 && input.currentGpa <= 4
    ? input.currentGpa * currentCredits
    : 0;
  const gradedCredits = currentCredits + planned.gradedCredits;
  const gradePoints = currentGradePoints + planned.gradePoints;
  const rawGpa = gradedCredits > 0 ? gradePoints / gradedCredits : null;

  return {
    rawGpa,
    displayGpa: formatRutgersGpa(rawGpa),
    gradedCredits,
    gradePoints,
    excludedCredits: planned.excludedCredits,
    invalidEntries: planned.invalidEntries,
  };
}

export type CourseGradeMode = 'weighted' | 'points';

export interface CourseAssessment {
  id: string;
  name: string;
  weightOrPoints: number;
  earned?: number | null;
}

export interface CourseGradeResult {
  currentPercent: number | null;
  requiredRemainingPercent: number | null;
  completedShare: number;
  remainingShare: number;
  status: 'achievable' | 'already-met' | 'impossible' | 'incomplete' | 'invalid';
}

export function calculateCourseGrade(
  mode: CourseGradeMode,
  assessments: CourseAssessment[],
  targetPercent: number,
): CourseGradeResult {
  if (!Number.isFinite(targetPercent) || targetPercent < 0 || targetPercent > 100 || assessments.length === 0) {
    return { currentPercent: null, requiredRemainingPercent: null, completedShare: 0, remainingShare: 0, status: 'invalid' };
  }

  const valid = assessments.filter((item) => Number.isFinite(item.weightOrPoints) && item.weightOrPoints > 0);
  if (valid.length !== assessments.length) {
    return { currentPercent: null, requiredRemainingPercent: null, completedShare: 0, remainingShare: 0, status: 'invalid' };
  }

  const totalShare = valid.reduce((sum, item) => sum + item.weightOrPoints, 0);
  const completed = valid.filter((item) => item.earned != null && Number.isFinite(Number(item.earned)));
  const remaining = valid.filter((item) => item.earned == null || !Number.isFinite(Number(item.earned)));
  const completedShare = completed.reduce((sum, item) => sum + item.weightOrPoints, 0);
  const remainingShare = remaining.reduce((sum, item) => sum + item.weightOrPoints, 0);

  if (mode === 'weighted' && Math.abs(totalShare - 100) > 0.001) {
    return { currentPercent: null, requiredRemainingPercent: null, completedShare, remainingShare, status: 'invalid' };
  }

  if (completed.some((item) => Number(item.earned) < 0 || (mode === 'weighted' && Number(item.earned) > 100))) {
    return { currentPercent: null, requiredRemainingPercent: null, completedShare, remainingShare, status: 'invalid' };
  }

  const earnedContribution = mode === 'weighted'
    ? completed.reduce((sum, item) => sum + Number(item.earned) * item.weightOrPoints / 100, 0)
    : completed.reduce((sum, item) => sum + Number(item.earned), 0);
  const currentPercent = completedShare > 0
    ? mode === 'weighted'
      ? earnedContribution / completedShare * 100
      : earnedContribution / completedShare * 100
    : null;

  if (remainingShare <= 0) {
    const finalPercent = mode === 'weighted' ? earnedContribution : earnedContribution / totalShare * 100;
    return {
      currentPercent: finalPercent,
      requiredRemainingPercent: null,
      completedShare,
      remainingShare,
      status: finalPercent >= targetPercent ? 'already-met' : 'impossible',
    };
  }

  const targetContribution = mode === 'weighted' ? targetPercent : targetPercent / 100 * totalShare;
  const requiredRemainingPercent = (targetContribution - earnedContribution) / remainingShare * 100;
  const status = requiredRemainingPercent <= 0
    ? 'already-met'
    : requiredRemainingPercent > 100
      ? 'impossible'
      : completed.length === 0
        ? 'incomplete'
        : 'achievable';

  return { currentPercent, requiredRemainingPercent, completedShare, remainingShare, status };
}
